import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Text,
  Stack,
  useMantineColorScheme,
  Box,
  Group,
  Tabs,
  TextInput,
  Center,
  ActionIcon
} from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { IconSearch, IconCalendar, IconRun, IconSettings, IconTrophy, IconArrowLeft } from '@tabler/icons-react';
import { dashboardService } from '../services/dashboardService';
import { HistorialFiltradoDTO, ExamenHistorialItem, TipoExamenDTO } from '../types/dashboard';
import PageSkeleton from '../components/ui/PageSkeleton';

const HistorialCompletoPage: React.FC = () => {
  const { colorScheme } = useMantineColorScheme();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState<string>('todos');
  const [tipos, setTipos] = useState<TipoExamenDTO[]>([]);
  const [conteos, setConteos] = useState<Record<string, number>>({});
  const [examenes, setExamenes] = useState<ExamenHistorialItem[]>([]);
  const [fechaDesde, setFechaDesde] = useState<Date | null>(null);
  const [fechaHasta, setFechaHasta] = useState<Date | null>(null);
  const [modoDia, setModoDia] = useState<boolean>(false);

  // Util para slugificar (para comparar tipos)
  const slug = (s: string) => (s || '').normalize('NFD').replace(/\p{Diacritic}/gu, '').toLowerCase().replace(/[^a-z0-9]+/g, '');

  // Cargar tipos activos
  useEffect(() => {
    const loadTipos = async () => {
      try {
        const data: TipoExamenDTO[] = await dashboardService.getTiposExamenActivos();
        setTipos(data || []);
      } catch (e) {
        console.warn('No se pudieron cargar los tipos de examen');
      }
    };
    loadTipos();
  }, []);

  // Construye fechas a YYYY-MM-DD en local
  const toYMD = (d: Date) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const da = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${da}`;
  };

  // Cargar historial filtrado cuando cambian filtros
  useEffect(() => {
    const controller = new AbortController();
    const timeout = setTimeout(async () => {
      try {
        setLoading(true);
        // Encontrar el tipo original por slug
        const tipoOriginal = tipos.find((t) => slug(t.nombre) === activeTab)?.nombre || null;
        const params = {
          tipo: activeTab !== 'todos' ? tipoOriginal : null,
          desde: fechaDesde ? toYMD(fechaDesde) : null,
          hasta: (modoDia && fechaDesde) ? toYMD(fechaDesde) : (fechaHasta ? toYMD(fechaHasta) : null),
          q: searchTerm || null,
        };
        const data: HistorialFiltradoDTO = await dashboardService.getHistorialFiltrado(params);
        console.log('Historial filtrado recibido:', data);
        console.log('Params enviados:', params);
        // Mapear conteos a slugs
        const mapped: Record<string, number> = {};
        Object.entries(data.conteoPorTipo || {}).forEach(([k, v]) => {
          const s = slug(k);
          console.log(`Tipo "${k}" -> slug "${s}" = ${v}`);
          mapped[s] = v as number;
        });
        setConteos(mapped);
        setExamenes(data.examenes || []);
      } catch (error) {
        console.error('Error cargando historial filtrado:', error);
        setConteos({});
        setExamenes([]);
      } finally {
        setLoading(false);
      }
    }, 300); // debounce búsqueda

    return () => {
      clearTimeout(timeout);
      controller.abort();
    };
  }, [activeTab, fechaDesde, fechaHasta, searchTerm, modoDia, tipos]);

  // Ya no necesitamos filtrar localmente, el backend devuelve todo filtrado

  const renderExamen = (examen: ExamenHistorialItem, index: number) => {
    const puntaje = examen.puntuacion || examen.puntajeFinal || 0;
    // Detectar tipo de examen
    const tipo = (examen.tipo || examen.tipoExamen || '').toLowerCase();
    const esRapido = tipo.includes('rápido') || tipo.includes('rapido');
    const esENARM = tipo.includes('enarm') || tipo.includes('simulacro');
    const IconComponent = esRapido ? IconRun : esENARM ? IconTrophy : IconSettings;
    const iconColor = esRapido ? '#60a5fa' : esENARM ? '#f87171' : '#f59e0b';
    
    return (
      <Box 
        key={index} 
        style={{
          padding: '12px',
          backgroundColor: colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
          borderRadius: '12px',
          border: `1px solid ${colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)'}`,
          cursor: 'pointer',
          transition: 'all 0.15s ease',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)';
        }}
        onClick={() => {
          navigate(`/estudiante/resultados/${examen.intentoId}`);
        }}
      >
        <Group justify="space-between" align="center" wrap="nowrap" mb="sm">
          {/* Icono y título */}
          <Group gap="xs" style={{ flex: 1, minWidth: 0 }} align="flex-start">
            <Box
              style={{
                width: '36px',
                height: '36px',
                borderRadius: '8px',
                backgroundColor: colorScheme === 'dark'
                  ? `${iconColor}20`
                  : `${iconColor}10`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <IconComponent size={20} style={{ color: iconColor }} />
            </Box>

            <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
              <Text size="sm" fw={600} ta="left" style={{
                color: colorScheme === 'dark' ? '#e2e8f0' : '#2d2a26',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}>
                {examen.nombreExamen}
              </Text>
              <Group gap="sm">
                <Text size="xs" ta="left" style={{ color: colorScheme === 'dark' ? '#64748b' : '#94a3b8' }}>
                  {examen.fechaFormatted || (examen.fecha ? dashboardService.formatDate(examen.fecha) : 'N/A')}
                </Text>
                <Text size="xs" ta="left" style={{ color: colorScheme === 'dark' ? '#64748b' : '#94a3b8' }}>
                  {examen.duracionFormatted || (examen.duracionMinutos ? dashboardService.formatStudyTime(examen.duracionMinutos) : 'N/A')}
                </Text>
                <Text size="xs" ta="left" style={{ color: colorScheme === 'dark' ? '#64748b' : '#94a3b8' }}>
                  {examen.descripcionPreguntas || `${examen.preguntasCorrectas || 0}/${examen.totalPreguntas || 0} preguntas`}
                </Text>
              </Group>
            </Stack>
          </Group>

          {/* Porcentaje */}
          <Text
            size="lg"
            fw={700}
            ta="right"
            style={{
              color: puntaje >= 70 ? '#22C55E' : puntaje >= 50 ? '#f59e0b' : '#EF4444',
              flexShrink: 0,
            }}
          >
            {Math.round(puntaje)}%
          </Text>
        </Group>

        {/* Barra de progreso visual */}
        <Box style={{
          width: '100%',
          height: '6px',
          backgroundColor: colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
          borderRadius: '3px',
          overflow: 'hidden',
        }}>
          <Box style={{
            width: `${Math.round(puntaje)}%`,
            height: '100%',
            backgroundColor: puntaje >= 70
              ? '#22C55E'
              : puntaje >= 50
                ? '#f59e0b'
                : '#EF4444',
            borderRadius: '3px',
            transition: 'width 0.3s ease',
          }} />
        </Box>
      </Box>
    );
  };

  if (loading) {
    return <PageSkeleton variant="list" />;
  }

  return (
    <Box style={{
      minHeight: '100vh',
      backgroundColor: colorScheme === 'dark' ? '#0f172a' : '#f7f3ee',
      padding: '16px 24px',
      position: 'relative',
    }}>
        {/* Header */}
        <Group justify="space-between" align="center" mb="xl">
          <Box>
            <Text size="xl" fw={700} ta="left" style={{
              color: colorScheme === 'dark' ? '#e2e8f0' : '#1e293b',
              fontFamily: 'Space Grotesk, Inter, sans-serif',
            }}>
              Historial Completo de Exámenes
            </Text>
          </Box>

          <ActionIcon 
            variant="default" 
            size="lg" 
            radius="md" 
            onClick={() => navigate(-1)}
          >
            <IconArrowLeft size={18} />
          </ActionIcon>
        </Group>

        {/* Buscador */}
        <TextInput
          placeholder="Buscar por nombre..."
          leftSection={<IconSearch size={16} />}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          mb="md"
          styles={{
            input: {
              backgroundColor: colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.05)' : '#ffffff',
              border: `1px solid ${colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
              color: colorScheme === 'dark' ? '#e2e8f0' : '#1e293b',
            }
          }}
        />

        {/* Filtros de fecha */}
        <Stack mb="lg" gap="sm">
          <Group>
            <Text size="sm" fw={500} style={{ color: colorScheme === 'dark' ? '#e2e8f0' : '#1e293b' }}>
              Buscar por:
            </Text>
            <Group gap="xs">
              <Box
                onClick={() => { setModoDia(false); setFechaHasta(null); }}
                style={{
                  cursor: 'pointer',
                  padding: '6px 12px',
                  borderRadius: '8px',
                  backgroundColor: !modoDia 
                    ? (colorScheme === 'dark' ? 'rgba(59, 130, 246, 0.2)' : 'rgba(59, 130, 246, 0.1)')
                    : 'transparent',
                  border: `1px solid ${!modoDia ? '#3b82f6' : (colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)')}`,
                  transition: 'all 0.2s',
                }}
              >
                <Text size="xs" fw={!modoDia ? 600 : 400} style={{ color: !modoDia ? '#3b82f6' : (colorScheme === 'dark' ? '#94a3b8' : '#64748b') }}>
                  Rango de fechas
                </Text>
              </Box>
              <Box
                onClick={() => { setModoDia(true); setFechaHasta(null); }}
                style={{
                  cursor: 'pointer',
                  padding: '6px 12px',
                  borderRadius: '8px',
                  backgroundColor: modoDia
                    ? (colorScheme === 'dark' ? 'rgba(59, 130, 246, 0.2)' : 'rgba(59, 130, 246, 0.1)')
                    : 'transparent',
                  border: `1px solid ${modoDia ? '#3b82f6' : (colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)')}`,
                  transition: 'all 0.2s',
                }}
              >
                <Text size="xs" fw={modoDia ? 600 : 400} style={{ color: modoDia ? '#3b82f6' : (colorScheme === 'dark' ? '#94a3b8' : '#64748b') }}>
                  Día específico
                </Text>
              </Box>
            </Group>
          </Group>
          
          <Group grow>
            <DateInput
              value={fechaDesde}
              onChange={(val: Date | null) => {
                setFechaDesde(val);
                if (modoDia) setFechaHasta(null);
              }}
              leftSection={<IconCalendar size={16} />}
              placeholder={modoDia ? "Selecciona un día" : "Desde fecha"}
              clearable
              valueFormat="DD/MM/YYYY"
              styles={{
                input: {
                  backgroundColor: colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.05)' : '#ffffff',
                  border: `1px solid ${colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
                  color: colorScheme === 'dark' ? '#e2e8f0' : '#1e293b',
                }
              }}
            />
            {!modoDia && (
              <DateInput
                value={fechaHasta}
                onChange={setFechaHasta}
                leftSection={<IconCalendar size={16} />}
                placeholder="Hasta fecha"
                clearable
                valueFormat="DD/MM/YYYY"
                minDate={fechaDesde || undefined}
                styles={{
                  input: {
                    backgroundColor: colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.05)' : '#ffffff',
                    border: `1px solid ${colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
                    color: colorScheme === 'dark' ? '#e2e8f0' : '#1e293b',
                  }
                }}
              />
            )}
          </Group>
        </Stack>

        {/* Pestañas por tipo */}
        <Tabs value={activeTab} onChange={(val) => { console.log('Cambiando tab a:', val); setActiveTab(val || 'todos'); }} mb="lg">
          <Tabs.List>
            <Tabs.Tab value="todos">Todos ({examenes.length})</Tabs.Tab>
            {tipos.map((t) => {
              const s = slug(t.nombre);
              const label = t.nombre;
              const count = conteos[s] || 0;
              return (
                <Tabs.Tab key={s} value={s}>{label} ({count})</Tabs.Tab>
              );
            })}
          </Tabs.List>
        </Tabs>

        {/* Lista de exámenes */}
        <Stack gap="md">
          {examenes.length > 0 ? (
            examenes.map((examen, index) => renderExamen(examen, index))
          ) : (
            <Center style={{ padding: '60px' }}>
              <Text size="sm" style={{ color: colorScheme === 'dark' ? '#94a3b8' : '#64748b' }}>
                No se encontraron exámenes
              </Text>
            </Center>
          )}
        </Stack>
    </Box>
  );
};

export default HistorialCompletoPage;
